import { Dispatch, compose } from 'redux';
/**
 *
 * CheckoutPage
 *
 */
import Button from 'components/Button/Button';
import Checkout from 'components/Checkout/Checkout';
import PageHeader from 'components/PageHeader/PageHeader';
import { push } from 'connected-react-router';
import { updateStoreBasicInfo } from 'containers/MainLayout/actions';
import {
  selectCart,
  selectOrderDisabled,
  selectPendingApprovalMode,
  selectRecommendNewWardForStore,
  selectStore,
} from 'containers/MainLayout/selectors';
import moment from 'moment';
import React, { FC, Fragment, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { Location, OrderDisabledReason, UpdateStoreBasicInfoInput, Ward } from 'types/schema';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import { applyVoucher, goToStep, setCheckoutStatus, trackOrdersCompletedById } from './actions';
import EmptyCartImage from './EmptyCart.svg';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';
import selectCheckoutPage, { selectCheckoutStatus } from './selectors';
import { RootState } from './types';

const OrderCompleteModal = React.lazy(() => import('./OrderComplete'));
const ForceUpdateNewAddressModal = React.lazy(() => import('./ForceUpdateNewAddressModal'));

interface OwnProps {
  checkoutpage: any;
  cart: any;
  isPendingApproval: boolean;
  checkoutStatus: any;
  orderDisabled: {
    orderDisabled: boolean;
    message: string;
    reason?: OrderDisabledReason;
  };
  store: any;
  recommendNewWardForStore?: Ward[];
}

interface StateProps {}

interface DispatchProps {
  onGoToCheckoutStep: (step: number) => void;
  onGoToOrders: (isPendingApproval: boolean) => void;
  onTrackOrdersCompleted: (ordersId) => void;
  onApplyVoucher: (data: any) => void;
  updateStoreBasicInfo: (data: Omit<UpdateStoreBasicInfoInput, 'storeUuid'>, cb?: (success: boolean) => void) => void;
}

type Props = StateProps & DispatchProps & OwnProps;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media (max-width: 991px) {
    align-items: unset;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  ${media.sm`
  flex-direction: row;
  `};
`;

const Space = styled.div`
  width: 15px;
  height: 15px;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  h2 {
    font-size: 32px;
    margin-top: 30pt;
    margin-bottom: 20pt;
  }
  img {
    margin-top: 100pt;
    max-width: 100vw;
  }
  p {
    font-size: 14px;
    margin-bottom: 12pt;
  }
`;

const CheckoutPage: FC<Props> = (props) => {
  const {
    checkoutpage,
    onGoToCheckoutStep,
    onGoToOrders,
    cart,
    checkoutStatus,
    isPendingApproval,
    orderDisabled,
    onTrackOrdersCompleted,
    onApplyVoucher,
    store,
    recommendNewWardForStore,
    updateStoreBasicInfo,
  } = props;

  const [openForceUpdateAddressModal, setOpenForceUpdateAddressModal] = useState(false);

  useEffect(() => {
    if (orderDisabled?.orderDisabled && orderDisabled?.reason === OrderDisabledReason.OutdatedWard) {
      setOpenForceUpdateAddressModal(true);
    } else {
      setOpenForceUpdateAddressModal(false);
    }
  }, [orderDisabled]);

  const isCheckoutSuccess = useMemo(
    () => checkoutStatus && checkoutStatus.status === 'CHECKOUT_SUCCESS',
    [checkoutStatus],
  );

  const handleTrackOrderCompleted = useCallback(
    (orders) => {
      if (orders.length) {
        onTrackOrdersCompleted(orders.map(({ id }) => id));
      }
    },
    [onTrackOrdersCompleted],
  );

  const handleGoToOrders = useCallback(() => {
    onGoToOrders(isPendingApproval);
  }, [isPendingApproval, onGoToOrders]);

  useEffect(() => {
    if (window.location.href.indexOf('/step/1') < 0 && !checkoutpage?.checkout?.total && !checkoutpage.loading) {
      onGoToCheckoutStep(1);
    }
  }, [checkoutpage?.checkout?.total, checkoutpage.loading, onGoToCheckoutStep]);

  useEffect(() => {
    if (!cart.cartSet.length && !cart.loading) {
      onApplyVoucher({});
    }
  }, [cart.loading, cart.cartSet.length]);

  return (
    <Fragment>
      {!cart.cartSet.length && !cart?.loading ? (
        <Container>
          <Empty>
            <h2>
              <FormattedMessage {...messages.emptyCart} />
            </h2>
            <p>
              <FormattedMessage {...messages.goBacktoMartPrompt} />
            </p>
            <ButtonContainer>
              <Link to="/market">
                <FormattedMessage {...messages.goToMart}>
                  {(goToMart) => (
                    <FormattedMessage {...utilsMessages.language}>
                      {(language: string) => (
                        <Button type="primary" width={language === 'vi' ? '175px' : '140px'}>
                          {goToMart}
                        </Button>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              </Link>
              <Space />
              <Link to="/favorite">
                <FormattedMessage {...messages.goToFavorites}>
                  {(goToFavorites) => (
                    <FormattedMessage {...utilsMessages.language}>
                      {(language: string) => (
                        <Button type="primary" width={language === 'vi' ? '175px' : '140px'}>
                          {goToFavorites}
                        </Button>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              </Link>
            </ButtonContainer>
            <img src={EmptyCartImage} alt="empty cart" />
          </Empty>
        </Container>
      ) : (
        <Container>
          <PageHeader center>
            <FormattedMessage {...messages.header} />
          </PageHeader>
          <Checkout
            loading={checkoutpage?.loading}
            onGoToCheckoutStep={onGoToCheckoutStep}
            orderDraftToken={checkoutpage?.orderDraftToken}
            subTotal={checkoutpage?.checkout?.total}
            orderDisabled={orderDisabled.orderDisabled}
            orderDisabledMessage={orderDisabled.message}
            orderDisabledReason={orderDisabled.reason}
            cart={cart}
            setOpenForceUpdateAddressModal={setOpenForceUpdateAddressModal}
          />
        </Container>
      )}
      <Suspense fallback={null}>
        <OrderCompleteModal
          isOpen={isCheckoutSuccess}
          isPendingApproval={isPendingApproval}
          onSubmit={handleGoToOrders}
          orders={checkoutStatus.orders}
          onTrackOrdersCompleted={handleTrackOrderCompleted}
        />
      </Suspense>
      {openForceUpdateAddressModal && (
        <Suspense fallback={null}>
          <ForceUpdateNewAddressModal
            openModal={openForceUpdateAddressModal}
            currentStoreAddress={{
              ...(store?.location as Location),
              noteForAddress: store?.addressNote?.trim() || '',
            }}
            storeName={store?.name || ''}
            onClose={() => {
              setOpenForceUpdateAddressModal(false);
            }}
            recommendNewWardForStore={recommendNewWardForStore || []}
            updateStoreBasicInfo={updateStoreBasicInfo}
          />
        </Suspense>
      )}
    </Fragment>
  );
};

const mapStateToProps = createStructuredSelector<RootState, StateProps>({
  checkoutpage: selectCheckoutPage(),
  cart: selectCart(),
  checkoutStatus: selectCheckoutStatus(),
  isPendingApproval: selectPendingApprovalMode(),
  orderDisabled: selectOrderDisabled(),
  store: selectStore(),
  recommendNewWardForStore: selectRecommendNewWardForStore(),
});

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    onGoToCheckoutStep: (step: number) => dispatch(goToStep(step)),
    onGoToOrders: (isPendingApproval: boolean) => {
      if (isPendingApproval) {
        dispatch(push(`/orders?statuses=PENDING_APPROVAL`));
      } else {
        const today = moment();
        dispatch(
          push(
            `/orders?orderDateStart=${today.startOf('day').toISOString()}&orderDateEnd=${today
              .endOf('day')
              .toISOString()}`,
          ),
        );
      }
      dispatch(
        setCheckoutStatus({
          status: 'DRAFT',
        }),
      );
    },
    onTrackOrdersCompleted: (orderIds) => dispatch(trackOrdersCompletedById(orderIds)),
    onApplyVoucher: (data: any) => dispatch(applyVoucher(data)),
    updateStoreBasicInfo: (data: Omit<UpdateStoreBasicInfoInput, 'storeUuid'>, cb?: (success: boolean) => void) =>
      dispatch(updateStoreBasicInfo(data, cb)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// <OwnProps> restricts access to the HOC's other props. This component must not do anything with reducer hoc
const withReducer = injectReducer<OwnProps>({
  key: 'checkoutPage',
  reducer: reducer,
});
// <OwnProps> restricts access to the HOC's other props. This component must not do anything with saga hoc
const withSaga = injectSaga<OwnProps>({ key: 'checkoutPage', saga: saga });

export default compose(withReducer, withSaga, withConnect)(CheckoutPage);
